/**
 * **listto** gamification — app-specific domain layer.
 *
 * The core (`engine.ts`) is agnostic; here we define WHICH listto events
 * grant rewards and how they are derived from the `activity` substrate (local-first
 * oplog, ~90 days in the PowerSync `activity_recent` bucket). Deriving the
 * `PlayerState` from the existing feed requires NO new sync column:
 * the reducer is pure and deterministic, recomputed on the client.
 *
 * **Positive and optional** gamification: XP for completing tasks, a gentle
 * streak of "productive days", and consistency badges. Nothing punitive; private
 * leagues by default (the core only ranks opt-in).
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState } from "./engine";
import { computeStreak, dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** listto streak line: "days with at least one completed task". */
export const STREAK_PRODUCTIVITY = "productivity";

/**
 * Semantic key for a domain event from the activity row.
 * Reimplements (purely, without i18n) the app's `activitySemanticKey` refinement:
 * distinguishes complete/reopen/trash by inspecting the patch.
 */
function semanticKey(row: Pick<ActivityRow, "entity_type" | "action" | "patch">): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "todo" && action === "updated" && patch.completed) {
    return patch.completed.new ? "completed" : "reopened";
  }
  if (action === "deleted") {
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

/**
 * Derives the `GameEvent` from an activity row, or `null` if it grants nothing.
 * The `type` is `"{entity_type}.{semanticKey}"` (e.g. `"todo.completed"`).
 * The `day` (for streaks) comes from `client_ts` (or `server_ts`) in UTC.
 */
export function activityRowToGameEvent(row: ActivityRow): GameEvent | null {
  if (!row.entity_type) {
    return null;
  }
  const key = semanticKey(row);
  const type = `${row.entity_type}.${key}`;
  const stamp = row.client_ts ?? row.server_ts;
  const at = stamp ? Date.parse(stamp) : Number.NaN;
  if (Number.isNaN(at)) {
    return null;
  }
  return { type, at, day: dayKeyUTC(at) };
}

/** Activity rows (feed already loaded local-first) → game events in order. */
export function activityRowsToGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  // The feed arrives server_ts DESC; the reducer is commutative for XP/counters
  // but streaks are recomputed over the set of days, so order is
  // irrelevant. Still, we emit in ascending chronological order.
  for (const row of rows.toReversed()) {
    const ev = activityRowToGameEvent(row);
    if (ev) {
      events.push(ev);
    }
  }
  return events;
}

/**
 * listto `GamificationConfig`. Only "positive" events grant XP; reopening,
 * editing, or deleting incurs no penalty (0 XP). Gentle streak: completing a task
 * marks the day as productive.
 */
export const LISTTO_GAMIFICATION: GamificationConfig = {
  xp: {
    "todo.completed": 10,
    "todo.created": 2,
    "subtask.completed": 3,
    "list.created": 5,
  },
  streakLines: {
    "todo.completed": [STREAK_PRODUCTIVITY],
  },
  counters: {
    "todo.completed": ["tasks_done"],
    "todo.created": ["tasks_created"],
    "list.created": ["lists_created"],
  },
  badges: [
    { id: "first-task", title: "Primer paso", when: counterAtLeast("tasks_done", 1) },
    { id: "ten-tasks", title: "En marcha", when: counterAtLeast("tasks_done", 10) },
    { id: "fifty-tasks", title: "Imparable", when: counterAtLeast("tasks_done", 50), bonusXp: 25 },
    {
      id: "century",
      title: "Centenario",
      when: counterAtLeast("tasks_done", 100),
      bonusXp: 50,
    },
    {
      id: "week-streak",
      title: "Semana productiva",
      when: streakAtLeast(STREAK_PRODUCTIVITY, 7),
      bonusXp: 40,
    },
    {
      id: "month-streak",
      title: "Mes en racha",
      when: streakAtLeast(STREAK_PRODUCTIVITY, 30),
      bonusXp: 100,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-3",
      title: "3 tareas hoy",
      period: "daily",
      goals: [{ metric: "todo.completed", target: 3 }],
      bonusXp: 15,
    },
    {
      id: "weekly-15",
      title: "15 tareas esta semana",
      period: "weekly",
      goals: [{ metric: "todo.completed", target: 15 }],
      bonusXp: 40,
    },
  ],
};

/** Badge catalog (for rendering badges not yet earned, in gray). */
export const LISTTO_BADGES = LISTTO_GAMIFICATION.badges ?? [];

// ── LOCAL-FIRST derivation from the `todo` table ───────────────────────────
//
// `activity_recent` is a server-PROJECTED bucket (read-only, arrives via
// sync), so it provides no instant feedback and does not exist for guests. The
// `todo` table is live local-first: completing a task raises XP instantly.
// This is the UI source; the mapping from `activity` is retained for
// server-side history/anti-fraud.

/** Minimum `todo` row needed by gamification (a schema subset). */
export interface GamifiableTodo {
  completed: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Derives game events from live (non-deleted) `todo` rows. Each task emits
 * `todo.created` (from `created_at`) and, when completed, `todo.completed`
 * (day ≈ `updated_at`, the last time it was touched). Pure and deterministic.
 */
export function todoRowsToGameEvents(todos: readonly GamifiableTodo[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const t of todos) {
    if (t.created_at) {
      const at = Date.parse(t.created_at);
      if (!Number.isNaN(at)) {
        events.push({ type: "todo.created", at, day: dayKeyUTC(at) });
      }
    }
    if (t.completed) {
      const stamp = t.updated_at ?? t.created_at;
      const at = stamp ? Date.parse(stamp) : Number.NaN;
      if (!Number.isNaN(at)) {
        events.push({ type: "todo.completed", at, day: dayKeyUTC(at) });
      }
    }
  }
  // Ascending chronological order (irrelevant for XP/counters; sorts streaks).
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recomputes player state from the `todo` table (local-first, instant
 * feedback). Reanchors the productivity streak to the real "today". Pure.
 *
 * `freezes` are the AVAILABLE streak freezes. This is the growth kit entry
 * point: a rewarded ad that grants `streak-freeze` is translated here
 * (`adFreezes(rewardsStore)`) without the gamification engine knowing that
 * ads exist.
 */
export function computePlayerFromTodos(
  todos: readonly GamifiableTodo[],
  todayKey: string = dayKeyUTC(Date.now()),
  freezes = 0,
): { state: PlayerState; events: number } {
  const events = todoRowsToGameEvents(todos);
  const { state } = applyEvents(initialState(freezes), events, LISTTO_GAMIFICATION);
  const active = state.activeDays[STREAK_PRODUCTIVITY] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_PRODUCTIVITY] = streak;
  }
  return { state, events: events.length };
}

/**
 * Full recompute of player state from the activity feed.
 * Pure and deterministic. `todayKey` allows fixing "today" (tests / time zone);
 * by default the current UTC date is used.
 */
export function computePlayerFromActivity(
  rows: readonly ActivityRow[],
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = activityRowsToGameEvents(rows);
  const { state } = applyEvents(initialState(), events, LISTTO_GAMIFICATION);
  // The engine anchors the streak to the last active day (so it does not break
  // "today without marking"). For the UI we reanchor to the real "today": if the
  // last productive day is far away, the current streak dies (best is preserved).
  const active = state.activeDays[STREAK_PRODUCTIVITY] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_PRODUCTIVITY] = streak;
  }
  return { state, events: events.length };
}
